import { Cross } from '@upright/ui/icons';
import { type ReactNode, type RefObject, useId } from 'react';
import { OverlayArrow, Popover as ReactAriaPopover } from 'react-aria-components';
import { IconButton } from '../button';
import { Stack } from '../layout';
import { Heading, HeadingGroup, Paragraph } from '../typography';
import styles from './Callout.module.css';

export interface Props {
  /** The control the callout points at. It stays usable while the callout is open. */
  triggerRef: RefObject<Element | null>;
  isOpen: boolean;
  /** What the reader is being told, as a statement: "This is the new Upright interface". */
  title: string;
  /** One or two sentences: where the thing is and what to do with it. */
  children: ReactNode;
  /** An optional action that takes the reader to the thing pointed at, such as opening its menu. */
  action?: ReactNode;
  /**
   * The ×, Escape while focus is inside the callout, and a scroll of the document, which closes any
   * non-modal React Aria popover. The caller decides whether the callout ever comes back.
   */
  onDismiss: () => void;
  /** Where the callout sits against the trigger. Default: below it, aligned to its far edge. */
  placement?: 'bottom end' | 'bottom start' | 'bottom';
  /**
   * The distance from the trigger to the card, in pixels, with the arrow inside it. Default: React
   * Aria's 8. Give more when a line runs just past the trigger, such as a bar's bottom border, so the
   * arrow sits on white below it rather than on top of it, where it cannot be seen.
   */
  offset?: number;
}

/**
 * Points once at a control the reader has not found yet, without blocking the page. A popover
 * that is not modal: no underlay, no focus trap and no scroll lock, so the reader can keep working
 * and the callout waits until it is dismissed. It does not take focus either, because it opens on
 * its own rather than because the reader asked for it, and moving their focus would interrupt
 * whatever they were doing.
 */
export function Callout({
  triggerRef,
  isOpen,
  title,
  children,
  action,
  onDismiss,
  placement = 'bottom end',
  offset,
}: Props) {
  const titleId = useId();
  return (
    <ReactAriaPopover
      className={styles.callout}
      triggerRef={triggerRef}
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) {
          onDismiss();
        }
      }}
      isNonModal
      placement={placement}
      offset={offset}
    >
      <OverlayArrow className={styles.arrow}>
        <svg width={12} height={12} viewBox="0 0 12 12" aria-hidden="true">
          <path d="M0 0 L6 6 L12 0" />
        </svg>
      </OverlayArrow>
      <section className={styles.body} aria-labelledby={titleId}>
        <Stack gap="lg">
          <HeadingGroup>
            <Heading id={titleId} level={2} size="md" height="md" weight="semibold" color="gray-900">
              {title}
            </Heading>
            <Paragraph size="md" height="sm" color="gray-600">
              {children}
            </Paragraph>
          </HeadingGroup>
          {action !== undefined && <div className={styles.action}>{action}</div>}
        </Stack>
        <span className={styles.dismiss}>
          <IconButton variant="text" size="medium" icon={Cross} aria-label="Dismiss" onPress={onDismiss} />
        </span>
      </section>
    </ReactAriaPopover>
  );
}
